import { shortWallet } from './drones';
/**
 * NT3D share card (1200x630 SVG). Pure function, no imports: the same file is copied to
 * workers/share-cards/src/cardSvg.ts so the in-app preview and the generated image match.
 *
 * Holo HUD palette: navy/indigo base, cyan → violet → magenta accents, hot-pink headline.
 * Images (faction holo emblem, NT3D mark) must be passed as data: URIs so the SVG renders
 * standalone (inside <img>, canvas export, or as an image/svg+xml response).
 */

export interface ShareCardData {
  player: string;
  /** number of keys in this buy */
  keys: number;
  /** test FRAME spent (whole tokens, not wei) */
  spent: number;
  /** contract faction id 1-8 */
  faction: number;
  tookLead: boolean;
  factionLogo?: string;
  mark?: string;
}

export const CARD_FACTIONS: Record<number, { name: string; power: string; accent: string; file: string }> = {
  1: { name: 'Raven', power: 'Chaos Redistribution', accent: '#46def8', file: 'raven' },
  2: { name: 'Ape', power: 'Primal Momentum', accent: '#ff5a7a', file: 'ape' },
  3: { name: 'Moth', power: 'Fragile Flame', accent: '#ffd45a', file: 'moth' },
  4: { name: 'Viper', power: 'Venom Strike', accent: '#7dff9b', file: 'viper' },
  5: { name: 'Rabbit', power: 'Glitch Frenzy', accent: '#ff4fd8', file: 'rabbit' },
  6: { name: 'Wolf', power: 'Pack Howl', accent: '#9fb4ff', file: 'wolf' },
  7: { name: 'Fox', power: 'Cunning Gambit', accent: '#ff9a4a', file: 'fox' },
  8: { name: 'Owl', power: 'All-Seeing', accent: '#a77bff', file: 'owl' },
};

/** 122812345 → "122.8M"; 950 → "950"; 1234 → "1.23K". */
export function abbreviate(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const a = Math.abs(n);
  const units: [number, string][] = [[1e15, 'Q'], [1e12, 'T'], [1e9, 'B'], [1e6, 'M'], [1e3, 'K']];
  for (const [v, s] of units) {
    if (a >= v) {
      const x = n / v;
      const digits = Math.abs(x) >= 100 ? 1 : Math.abs(x) >= 10 ? 1 : 2;
      return `${x.toFixed(digits).replace(/\.?0+$/, '')}${s}`;
    }
  }
  return a >= 100 ? Math.round(n).toString() : String(Number(n.toFixed(2)));
}

const esc = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' }[c]!));
const short = (a: string) => (a && a.length > 12 ? shortWallet(a) : a || 'anon');

export function buildShareCardSvg(d: ShareCardData): string {
  const f = CARD_FACTIONS[d.faction] || CARD_FACTIONS[1];
  const head = d.tookLead ? "YOU'RE IN THE LEAD" : 'KEYS ACQUIRED';
  const font = "Orbitron, 'Exo 2', Rajdhani, 'Segoe UI', Arial, sans-serif";
  const mono = "'JetBrains Mono', 'Cascadia Mono', Consolas, 'Courier New', monospace";
  // Stats: three fixed columns, labels and values share one baseline each.
  const cols = [
    { x: 520, label: 'KEYS', value: abbreviate(d.keys), unit: '' },
    { x: 740, label: 'SPENT', value: abbreviate(d.spent), unit: 'test FRAME' },
    { x: 960, label: 'FACTION', value: f.name.toUpperCase(), unit: f.power },
  ];
  const stat = cols.map(c => `
    <text x="${c.x}" y="388" font-family="${mono}" font-size="15" letter-spacing="3" fill="#b3b8e6">${c.label}</text>
    <text x="${c.x}" y="442" font-family="${font}" font-weight="700" font-size="${c.value.length > 7 ? 36 : 44}" fill="#eef0ff">${esc(c.value)}</text>
    <text x="${c.x}" y="472" font-family="${mono}" font-size="14" fill="${c === cols[2] ? f.accent : '#46def8'}">${esc(c.unit)}</text>`).join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="1200" height="630" viewBox="0 0 1200 630">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#000312"/><stop offset=".55" stop-color="#070a26"/><stop offset="1" stop-color="#1d225b"/></linearGradient>
    <linearGradient id="acc" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#46def8"/><stop offset=".5" stop-color="#843ee1"/><stop offset="1" stop-color="#f058f6"/></linearGradient>
    <radialGradient id="halo" cx=".5" cy=".5" r=".5"><stop offset="0" stop-color="${f.accent}" stop-opacity=".35"/><stop offset=".6" stop-color="#843ee1" stop-opacity=".12"/><stop offset="1" stop-color="#000312" stop-opacity="0"/></radialGradient>
    <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M40 0H0V40" fill="none" stroke="#46def8" stroke-opacity=".07"/></pattern>
    <clipPath id="logoClip"><circle cx="230" cy="300" r="150"/></clipPath>
    <clipPath id="markClip"><rect x="470" y="70" width="64" height="64" rx="10"/></clipPath>
  </defs>
  <rect width="1200" height="630" fill="url(#bg)"/>
  <rect width="1200" height="630" fill="url(#grid)"/>
  <rect x="0" y="0" width="1200" height="6" fill="url(#acc)"/>
  <rect x="24" y="24" width="1152" height="582" rx="14" fill="none" stroke="url(#acc)" stroke-opacity=".55" stroke-width="2"/>

  <!-- faction emblem -->
  <circle cx="230" cy="300" r="200" fill="url(#halo)"/>
  <circle cx="230" cy="300" r="166" fill="#070a26" stroke="url(#acc)" stroke-width="4"/>
  <circle cx="230" cy="300" r="178" fill="none" stroke="#46def8" stroke-opacity=".25" stroke-dasharray="4 10"/>
  ${d.factionLogo ? `<image x="80" y="150" width="300" height="300" href="${d.factionLogo}" xlink:href="${d.factionLogo}" clip-path="url(#logoClip)" preserveAspectRatio="xMidYMid slice"/>` : `<text x="230" y="320" text-anchor="middle" font-family="${font}" font-size="64" font-weight="700" fill="${f.accent}">${esc(f.name[0])}</text>`}
  <text x="230" y="524" text-anchor="middle" font-family="${font}" font-weight="700" font-size="28" letter-spacing="6" fill="${f.accent}">${esc(f.name.toUpperCase())}</text>
  <text x="230" y="552" text-anchor="middle" font-family="${mono}" font-size="14" letter-spacing="2" fill="#b3b8e6">VARIANCE CELL</text>

  <!-- brand -->
  ${d.mark ? `<image x="470" y="70" width="64" height="64" href="${d.mark}" xlink:href="${d.mark}" clip-path="url(#markClip)"/>` : ''}
  <text x="${d.mark ? 552 : 470}" y="104" font-family="${font}" font-weight="800" font-size="30" letter-spacing="4" fill="#eef0ff">NT<tspan fill="#f058f6">3D</tspan></text>
  <text x="${d.mark ? 552 : 470}" y="128" font-family="${mono}" font-size="14" letter-spacing="4" fill="#b3b8e6">NO TOMORROW 3D</text>
  <rect x="938" y="84" width="200" height="34" rx="6" fill="none" stroke="#ffd45a" stroke-opacity=".7"/>
  <text x="1038" y="107" text-anchor="middle" font-family="${mono}" font-size="14" letter-spacing="2" fill="#ffd45a">TESTNET DEMO</text>

  <!-- headline -->
  <text x="470" y="222" font-family="${font}" font-weight="800" font-size="${d.tookLead ? 54 : 58}" letter-spacing="2" fill="#ff2d95">${head}</text>
  <text x="470" y="270" font-family="${mono}" font-size="20" letter-spacing="3" fill="#46def8">RESISTANCE MEMBER <tspan fill="#eef0ff">${esc(short(d.player))}</tspan></text>
  <rect x="470" y="300" width="668" height="2" fill="url(#acc)" opacity=".7"/>
  ${stat}
  <rect x="470" y="500" width="668" height="1" fill="#46def8" opacity=".25"/>
  <text x="470" y="548" font-family="${mono}" font-size="18" fill="#eef0ff">nt3d.fun <tspan fill="#b3b8e6">· every key adds 30s · last buyer wins</tspan></text>
  <text x="470" y="576" font-family="${mono}" font-size="13" fill="#b3b8e6">Free testnet demo. test FRAME has no value.</text>
</svg>`;
}
